import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { findReplace } from "../lib/replace";

const SAMPLE = "The quick brown fox. The lazy dog.";

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-2 text-sm ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border accent-primary"
      />
      {label}
    </label>
  );
}

export function FindReplaceTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [find, setFind] = useState("The");
  const [replace, setReplace] = useState("A");
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () =>
      findReplace(input, find, replace, {
        caseInsensitive,
        useRegex,
        wholeWord,
      }),
    [input, find, replace, caseInsensitive, useRegex, wholeWord],
  );

  const copy = async () => {
    if (!result.ok || !result.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.fr_input_label()}</Label>
          <textarea
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.fr_placeholder()}
            spellCheck={false}
            className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-find`}>{m.fr_find_label()}</Label>
            <Input
              id={`${uid}-find`}
              value={find}
              onChange={(e) => setFind(e.target.value)}
              spellCheck={false}
              className="font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-rep`}>{m.fr_replace_label()}</Label>
            <Input
              id={`${uid}-rep`}
              value={replace}
              onChange={(e) => setReplace(e.target.value)}
              spellCheck={false}
              className="font-mono"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          <Toggle
            checked={caseInsensitive}
            onChange={setCaseInsensitive}
            label={m.fr_case()}
          />
          <Toggle
            checked={useRegex}
            onChange={setUseRegex}
            label={m.fr_regex()}
          />
          <Toggle
            checked={wholeWord}
            onChange={setWholeWord}
            label={m.fr_whole_word()}
            disabled={useRegex}
          />
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-out`}>{m.fr_output_label()}</Label>
          <div className="flex items-center gap-3">
            {result.ok && (
              <span
                data-testid="fr-count"
                className="text-xs text-muted-foreground"
              >
                {m.fr_matches({ n: String(result.count) })}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              disabled={!result.ok || !result.text}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.fr_copied() : m.fr_copy()}
            </Button>
          </div>
        </div>
        {result.ok ? (
          <textarea
            id={`${uid}-out`}
            readOnly
            data-testid="fr-output"
            value={result.text}
            spellCheck={false}
            className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        ) : (
          <p
            id={`${uid}-out`}
            data-testid="fr-error"
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300"
          >
            {m.fr_error()}
          </p>
        )}
      </div>
    </div>
  );
}
